import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiMail, FiLock } from 'react-icons/fi';
import { Screen } from '../../components/ui/Screen';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { signInWithEmail } from '../../services/firebase/auth.service';
import { useAppContext } from '../../store/AppContext';

export function LoginScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: userLoading, authError, isDemoMode } = useAppContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [keepLoggedIn, setKeepLoggedIn] = useState(true);

  useEffect(() => {
    console.log('[LoginScreen] Updated version loaded');
  }, []);

  // ITEM 4: Demo mode redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const windowParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    
    const urlHasDemo = urlParams.get('demo') === '1';
    const windowHasDemo = windowParams.get('demo') === '1';
    const hashHasDemo = hashParams.get('demo') === '1';
    
    const shouldRedirect = isDemoMode || urlHasDemo || windowHasDemo || hashHasDemo;
    
    if (shouldRedirect) {
      navigate('/home?demo=1', { replace: true });
      return;
    }
  }, [isDemoMode, navigate, location.search]);

  // P0 FIX: Email verification is soft — verified or not, go to /home.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (!userLoading && user) {
      navigate('/home', { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [user, userLoading, navigate]);

  useEffect(() => {
    if (authError && !userLoading) {
      setError(authError.message);
    }
  }, [authError, userLoading]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !password) {
      setError('Please enter both email and password.');
      setLoading(false);
      return;
    }

    try {
      await signInWithEmail(email, password, keepLoggedIn);
      await new Promise((resolve) => setTimeout(resolve, 200));
      navigate('/home', { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const code = (err as { code?: string })?.code ?? '';
      const message = err instanceof Error ? err.message : '';
      const match = code || message;
      if (match.includes('auth/invalid-credential') || match.includes('auth/user-not-found') || match.includes('auth/wrong-password')) {
        setError('Incorrect email or password. Please try again.');
      } else if (match.includes('auth/invalid-email')) {
        setError('Please enter a valid email address.');
      } else if (match.includes('auth/too-many-requests')) {
        setError('Too many failed attempts. Please try again later.');
      } else if (match.includes('auth/network-request-failed')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  if (isDemoMode) return null;

  if (userLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) return null;

  return (
    <Screen title="Welcome back" subtitle="Sign in to continue your journey.">
      <Card>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Email"
            type="email"
            name="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            placeholder="you@example.com"
            leftIcon={<FiMail />}
            error={undefined}
            required
            disabled={loading}
          />
          <Input
            label="Password"
            type="password"
            name="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            placeholder="••••••••"
            leftIcon={<FiLock />}
            error={undefined}
            required
            disabled={loading}
          />
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="keepLoggedIn"
              checked={keepLoggedIn}
              onChange={(e) => setKeepLoggedIn(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 bg-white text-gray-900 accent-gray-900 focus:ring-1 focus:ring-gray-400 focus:ring-offset-0 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
              style={{ accentColor: '#111111' }}
              disabled={loading}
            />
            <label
              htmlFor="keepLoggedIn"
              className="text-xs text-gray-700 cursor-pointer select-none"
            >
              Keep me logged in on this device
            </label>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <Button fullWidth type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Continue'}
          </Button>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <Link to="/auth/forgot-password" className="hover:text-gray-900">
              Forgot password
            </Link>
            <Link to="/auth/register" className="hover:text-gray-900">
              Create account
            </Link>
          </div>
        </form>
      </Card>
    </Screen>
  );
}
